import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";

const EMPTY_PICK = {
  output_column: "",
  filter_column: "",
  filter_values: [],
  value_column: "",
};

const EMPTY_PICK_FIRST = {
  group_key: "",
  sort_column: "",
  sort_direction: "asc",
  picks: [{ ...EMPTY_PICK }],
};

const EMPTY_VALID_PERIOD = {
  from_column: "",
  to_column: "",
  format: "d.m.Y",
};

export default function SourceOptionsEditor({ source_options, onChange }) {
  const opts = source_options ?? {};
  const pick_first = opts.pick_first ?? null;

  const set = (key, value) => onChange({ ...opts, [key]: value || null });

  const setPickFirst = (pf) => onChange({ ...opts, pick_first: pf });

  const setPfField = (key, value) =>
    setPickFirst({ ...pick_first, [key]: value });

  const setPick = (index, key, value) => {
    const updated = pick_first.picks.map((p, i) =>
      i === index ? { ...p, [key]: value } : p
    );
    setPickFirst({ ...pick_first, picks: updated });
  };

  const addPick = () =>
    setPickFirst({
      ...pick_first,
      picks: [...pick_first.picks, { ...EMPTY_PICK }],
    });

  const removePick = (index) =>
    setPickFirst({
      ...pick_first,
      picks: pick_first.picks.filter((_, i) => i !== index),
    });

  const valid_period = pick_first?.valid_period ?? null;

  const setValidPeriodField = (key, value) =>
    setPickFirst({
      ...pick_first,
      valid_period: { ...valid_period, [key]: value },
    });

  const toggleValidPeriod = (enabled) => {
    if (enabled) {
      setPickFirst({ ...pick_first, valid_period: { ...EMPTY_VALID_PERIOD } });
    } else {
      const { valid_period: _removed, ...rest } = pick_first;
      setPickFirst(rest);
    }
  };

  const togglePickFirst = (enabled) => {
    if (enabled) {
      onChange({ ...opts, pick_first: { ...EMPTY_PICK_FIRST } });
    } else {
      const { pick_first: _removed, ...rest } = opts;
      onChange(Object.keys(rest).length ? rest : null);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Typography variant="subtitle2">
        Quelldatei-Optionen (optional)
      </Typography>

      <Box display="flex" gap={2} flexWrap="wrap">
        <TextField
          size="small"
          label="Tabellenblatt"
          placeholder="z.B. Tabelle1"
          value={opts.sheet_name ?? ""}
          onChange={(e) => set("sheet_name", e.target.value)}
          sx={{ width: 160 }}
        />
        <TextField
          size="small"
          label="Kopfzeile (Nr.)"
          type="number"
          inputProps={{ min: 1 }}
          value={opts.header_row ?? ""}
          onChange={(e) =>
            onChange({
              ...opts,
              header_row: e.target.value ? parseInt(e.target.value, 10) : null,
            })
          }
          sx={{ width: 120 }}
        />
        <TextField
          size="small"
          label="Trennzeichen"
          placeholder=";"
          value={opts.delimiter ?? ""}
          onChange={(e) => set("delimiter", e.target.value)}
          sx={{ width: 100 }}
        />
        <TextField
          size="small"
          label="Zeichenkodierung"
          placeholder="utf-8"
          value={opts.encoding ?? ""}
          onChange={(e) => set("encoding", e.target.value)}
          sx={{ width: 120 }}
        />
        <TextField
          size="small"
          label="Distinct-Spalte"
          placeholder="z.B. ID_SPARTE"
          value={opts.distinct_by ?? ""}
          onChange={(e) => set("distinct_by", e.target.value)}
          sx={{ width: 160 }}
        />
      </Box>

      <Divider />

      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(pick_first)}
              onChange={(e) => togglePickFirst(e.target.checked)}
            />
          }
          label={
            <Box>
              <Typography variant="subtitle2">
                Zeilenauswahl nach Priorität
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Gruppiert Zeilen nach einem Schlüssel, sortiert sie nach
                Priorität und wählt für jede Ausgabespalte den ersten gültigen
                Wert aus.
              </Typography>
            </Box>
          }
        />

        {pick_first && (
          <Box mt={2} display="flex" flexDirection="column" gap={2}>
            <Box display="flex" gap={2} flexWrap="wrap">
              <TextField
                size="small"
                label="Gruppen-Schlüssel"
                placeholder="z.B. ID_KUNDE"
                value={pick_first.group_key ?? ""}
                onChange={(e) => setPfField("group_key", e.target.value)}
                sx={{ width: 180 }}
              />
              <TextField
                size="small"
                label="Sortierspalte"
                placeholder="z.B. SORTNR"
                value={pick_first.sort_column ?? ""}
                onChange={(e) => setPfField("sort_column", e.target.value)}
                sx={{ width: 160 }}
              />
              <FormControl size="small" sx={{ width: 140 }}>
                <InputLabel>Sortierrichtung</InputLabel>
                <Select
                  value={pick_first.sort_direction ?? "asc"}
                  label="Sortierrichtung"
                  onChange={(e) => setPfField("sort_direction", e.target.value)}
                >
                  <MenuItem value="asc">Aufsteigend</MenuItem>
                  <MenuItem value="desc">Absteigend</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Picks — welcher Wert pro Ausgabespalte ausgewählt wird
              </Typography>

              {pick_first.picks.map((pick, index) => (
                <Box
                  key={index}
                  display="flex"
                  gap={1}
                  flexWrap="wrap"
                  alignItems="flex-start"
                  mb={1}
                >
                  <TextField
                    size="small"
                    label="Ausgabespalte"
                    placeholder="z.B. email"
                    value={pick.output_column}
                    onChange={(e) =>
                      setPick(index, "output_column", e.target.value)
                    }
                    sx={{ width: 140 }}
                  />
                  <TextField
                    size="small"
                    label="Filterspalte"
                    placeholder="z.B. TELEKOM_TYPEN"
                    value={pick.filter_column}
                    onChange={(e) =>
                      setPick(index, "filter_column", e.target.value)
                    }
                    sx={{ width: 180 }}
                  />
                  <TextField
                    size="small"
                    label="Filterwerte (kommagetrennt)"
                    placeholder='z.B. E-Mail, E-Mail (geschäftl.)'
                    value={(pick.filter_values ?? []).join(", ")}
                    onChange={(e) =>
                      setPick(
                        index,
                        "filter_values",
                        e.target.value
                          .split(",")
                          .map((v) => v.trim())
                          .filter(Boolean)
                      )
                    }
                    sx={{ width: 280 }}
                  />
                  <TextField
                    size="small"
                    label="Wertspalte"
                    placeholder="z.B. TELEKOMMUNIKATION"
                    value={pick.value_column}
                    onChange={(e) =>
                      setPick(index, "value_column", e.target.value)
                    }
                    sx={{ width: 200 }}
                  />
                  {pick_first.picks.length > 1 && (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => removePick(index)}
                      sx={{ alignSelf: "center", minWidth: 0, px: 1 }}
                    >
                      ×
                    </Button>
                  )}
                </Box>
              ))}

              <Button size="small" variant="outlined" onClick={addPick}>
                + Pick hinzufügen
              </Button>
            </Box>

            <Divider />

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(valid_period)}
                    onChange={(e) => toggleValidPeriod(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    Nur am Stichtag gültige Zeilen berücksichtigen
                  </Typography>
                }
              />

              {valid_period && (
                <Box mt={1} display="flex" gap={2} flexWrap="wrap">
                  <TextField
                    size="small"
                    label="Gültig-ab-Spalte"
                    placeholder="z.B. GUELTIG_VON"
                    value={valid_period.from_column ?? ""}
                    onChange={(e) =>
                      setValidPeriodField("from_column", e.target.value)
                    }
                    sx={{ width: 180 }}
                  />
                  <TextField
                    size="small"
                    label="Gültig-bis-Spalte"
                    placeholder="z.B. GUELTIG_BIS"
                    value={valid_period.to_column ?? ""}
                    onChange={(e) =>
                      setValidPeriodField("to_column", e.target.value)
                    }
                    sx={{ width: 180 }}
                  />
                  <TextField
                    size="small"
                    label="Datumsformat"
                    placeholder="d.m.Y"
                    value={valid_period.format ?? ""}
                    onChange={(e) =>
                      setValidPeriodField("format", e.target.value)
                    }
                    sx={{ width: 140 }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

SourceOptionsEditor.propTypes = {
  source_options: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};
